// This component is similar to HawkeyeV2CreateFilter except a few differences
// The key differences in this component are
// - This component does not call save filter it only updates the filters
// - It sets initial filter name and selected filter values from editingFilter prop

import React, { Fragment, memo, useMemo, useEffect, useCallback } from "react";

import { useAxiomState } from "__AxiomComponents/AxiomHooks/AxiomHooks";
import Icon from "../Icons/Icon";
import HawkeyeV2Selection from "../HawkeyeV2Selection/HawkeyeV2Selection";
import HawkeyeV2FilterSelection from "../HawkeyeV2FilterSelection/HawkeyeV2FilterSelection";

import Toggle from "react-toggle";
import "react-toggle/style.css";

import { getSelectedFilters, getAppliedFilters } from "../HawkeyeV2Filters/HawkeyeV2FilterUtils";

function HawkeyeV2EditFilter(props) {
  const {
    widget,
    editingFilter,
    filterOptions,
    smartFilterRefs,
    editFilter,
    closeEditFilter,
    HawkeyeV2AppSettings = {},
    showPublicToggle,
  } = props;

  const [state, dispatch] = useAxiomState(
    {
      selectedFilter: null,
      filterSaveName: "",
      filterValues: [],
      isFetchingValues: false,
      selectedFilters: [],
    },
    props.appName,
    props.appID,
    // HawkeyeV2CreateFilter is intentional as all the server events are same as create
    "HawkeyeV2CreateFilter",
    props.componentID
  );

  // Set filter values from selected filter for editing
  useEffect(() => {
    const updatedFilter = getAppliedFilters(editingFilter, [], filterOptions);
    const selectedFilters = updatedFilter[0].filterSettings;
    dispatch({
      filterSaveName: editingFilter.filterName,
      selectedFilters: selectedFilters,
      selectedFilter: selectedFilters[0].filter,
      isPublic: editingFilter.isPublic,
    });
    dispatch(
      {
        filters: selectedFilters,
        smartFilterRefs: smartFilterRefs,
      },
      "AxiomEvent",
      "onApplyFilter"
    );
  }, [dispatch, filterOptions, editingFilter, smartFilterRefs]);

  // Get filter values on selected filter change
  useEffect(() => {
    if (state.selectedFilter) {
      dispatch({
        isFetchingValues: true,
        filterValues: [],
      });
      dispatch(
        {
          filter: state.selectedFilter,
          smartFilterRefs: smartFilterRefs,
          HawkeyeV2AppSettings: HawkeyeV2AppSettings,
        },
        "AxiomEvent",
        "getFilterValues"
      );
    }
  }, [dispatch, state.selectedFilter, smartFilterRefs, HawkeyeV2AppSettings]);

  const onFilterSelection = useCallback(
    (selectedFilter) => {
      const d1 = { ...selectedFilter };
      delete d1["component"];
      dispatch({ selectedFilter: d1 });
    },
    [dispatch]
  );

  const onFilterValueSelection = useCallback(
    (selectedValues) => {
      const filterValues = selectedValues.map((value) => value.displayName);
      const newSelectedFilters = getSelectedFilters(
        state.selectedFilters,
        state.selectedFilter,
        filterValues
      );

      dispatch(
        {
          filters: [
            {
              filter: state.selectedFilter,
              values: filterValues,
            },
          ],
          smartFilterRefs: smartFilterRefs,
        },
        "AxiomEvent",
        "onApplyFilter"
      );

      dispatch({
        selectedFilters: newSelectedFilters.filter((f) => f.values.length),
        selectedFilter: null,
        filterValues: [],
      });
    },
    [dispatch, state.selectedFilter, state.selectedFilters, smartFilterRefs]
  );

  const resetSavedFilters = useCallback(() => {
    if (state.selectedFilters.length > 0) {
      dispatch(
        (state) => ({
          filters: state.selectedFilters,
          smartFilterRefs: smartFilterRefs,
        }),
        "AxiomEvent",
        "onRemoveFilter"
      );
    }
  }, [dispatch, smartFilterRefs, state.selectedFilters.length]);

  const closeEditWindow = useCallback(() => {
    resetSavedFilters();
    closeEditFilter();
  }, [closeEditFilter, resetSavedFilters]);

  const onEditFilter = () => {
    const { filterSaveName, selectedFilters } = state;
    editFilter(editingFilter.filterID, filterSaveName, selectedFilters, state.isPublic);
    closeEditWindow();
  };

  const updateFilterSaveName = (event) => dispatch({ filterSaveName: event.target.value });

  const computedFilterOptions = useMemo(
    () =>
      filterOptions.map((f) => {
        const filter = { ...f };
        const filterSelectedValues = state.selectedFilters.find((d) => d.filter.key === f.key);
        if (filterSelectedValues && filterSelectedValues.values.length > 0) {
          const selectedValueCount = filterSelectedValues.values.length;
          filter.component = (
            <Fragment>
              {filter.displayName}
              <b>{` (${selectedValueCount} selected)`}</b>
            </Fragment>
          );
        }
        return filter;
      }),
    [filterOptions, state.selectedFilters]
  );

  const selectedFilterValues = useMemo(() => {
    if (state.selectedFilter) {
      const filterSelectedValues = state.selectedFilters.find(
        (d) => d.filter.key === state.selectedFilter.key
      );
      if (filterSelectedValues) {
        return filterSelectedValues.values.map((v) => ({ displayName: v, key: v }));
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [state.selectedFilter, state.selectedFilters]);

  const filterSelectedOptions = useMemo(
    () => (state.selectedFilter ? [state.selectedFilter] : []),
    [state.selectedFilter]
  );

  const togglePublicFilter = useCallback(() => {
    dispatch({
      isPublic: !state.isPublic,
    });
  }, [dispatch, state.isPublic]);

  const icons = useMemo(() => {
    return {
      checked: <div className="unchecked-icon">{""}</div>,
      unchecked: <div>{""}</div>,
    };
  }, []);

  return (
    <div className="hawkeye-filter-modal">
      <div className="hawkeye-filter-modal-content">
        <div className="hawkeye-filter-modal-header">
          <div className="hawkeye-filter-modal-title">Edit filter {editingFilter.filterName}</div>
          {showPublicToggle != false && (
            <div className="hawkeye-filter-modal-toggle">
              <div
                className="hawkeye-filter-toggle-text"
                title="Public filter is visible to all users who have access to this widget"
              >
                <Icon icon="ICON_INFO" className="hawkeye-filter-tag-icon" />
              </div>
              <div className="hawkeye-filter-toggle-text">Public</div>
              <div className="hawkeye-filter-toggle-btn">
                <Toggle
                  checked={state.isPublic == 1 ? true : false}
                  icons={icons}
                  onChange={togglePublicFilter}
                />
              </div>
            </div>
          )}
        </div>
        <div className="hawkeye-filter-modal-body">
          <div className="hawkeye-filter-create">
            <div className="hawkeye-filter-save">
              <label className="hawkeye-filter-save-label">Enter a filter name: </label>
              <input
                type="text"
                value={state.filterSaveName}
                onChange={updateFilterSaveName}
                placeholder="Enter a name for the filter"
                className="hawkeye-filter-save-input"
              />
            </div>
            <div className="hawkeye-filter-create-selection">
              <HawkeyeV2Selection
                className="hawkeye-filter-create-section hawkeye-filter-create-dimensions"
                isSearchable={true}
                options={computedFilterOptions}
                selectedOptions={filterSelectedOptions}
                onSelection={onFilterSelection}
                appName={props.appName}
                appID={props.appID}
                componentID={props.componentID}
              />
              <HawkeyeV2FilterSelection
                isFetchingValues={state.isFetchingValues}
                filterValues={state.filterValues}
                selectedFilter={state.selectedFilter}
                selectedValues={selectedFilterValues}
                onSelection={onFilterValueSelection}
                appName={props.appName}
                appID={props.appID}
                componentID={`selection-value-${props.componentID}`}
              />
            </div>
          </div>
        </div>
        <div className="hawkeye-filter-modal-footer">
          <button onClick={closeEditWindow} className="hawkeye-btn hawkeye-btn-secondary">
            Close
          </button>
          <button
            disabled={!state.filterSaveName || state.selectedFilters.length === 0}
            onClick={onEditFilter}
            className="hawkeye-btn hawkeye-btn-primary"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(HawkeyeV2EditFilter);
